import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Typography, Grid, Paper, Button, ListItemText, IconButton, Icon, Divider } from "@mui/material";

// Your existing hooks and components
import { useProducts } from "../../hooks/useProducts";
import { useCart } from "../../hooks/useCart";
import { usePOSService } from "../../hooks/usePOSService";
import { useAuth } from "../../../auth/hooks/useAuth";
import { toast } from "react-toastify";

export const WaiterOrderScreen = () => {
    const { tableId } = useParams<{ tableId: string }>();
    const navigate = useNavigate();


    const { products, loading } = useProducts();
    const {
        items,
        addItem,
        updateQuantity,
        handleDeleteCartItem,
        subtotal,
        tax,
        total,
        clearCart,
    } = useCart();
    const { posService } = usePOSService();
    const { user } = useAuth();

    const handleAddProduct = (product: any) => {
        addItem(product);
    };

    const handleSendToKitchen = async () => {
        if (!tableId) {
            alert("No table selected");
            return;
        }

        try {
            await posService.sendOrderToKitchen({
                waiter_id: user?._id,
                items: items.map((item) => ({
                    product_id: item.product_id,
                    quantity: item.quantity,
                    price: item.price,
                    modifiers: item.modifiers || {},
                })),
            });

            toast.success("Order sent to kitchen!");
            clearCart();
            navigate("/pos/waiter"); // Back to dashboard
        } catch (err) {
            console.error("Error sending to kitchen:", err);
            toast.error("Failed to send order");
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Product List */}
            <Typography variant="h5" gutterBottom fontWeight={600}>
                Choose Items
            </Typography>

            {loading ? (
                <Grid container spacing={2}>
                    {[...Array(8)].map((_, i) => (
                        <Grid item xs={6} sm={4} md={3} lg={2} key={i}>
                            <Paper
                                elevation={1}
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    textAlign: "center",
                                    opacity: 0.6,
                                }}
                            >
                                <Typography fontWeight={600}>Loading...</Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    {products.map((product: any) => (
                        <Grid item xs={6} sm={4} md={3} lg={2} key={product._id}>
                            <Paper
                                elevation={1}
                                onClick={() => handleAddProduct(product)}
                                sx={{
                                    p: 2,
                                    cursor: "pointer",
                                    borderRadius: 2,
                                    "&:hover": {
                                        bgcolor: "#f5f5f5",
                                        transform: "scale(1.02)",
                                        transition: "all 0.2s",
                                    },
                                }}
                            >
                                <Typography fontWeight={600}>{product.name}</Typography>
                                <Typography color="text.secondary">
                                    BHD {product.price?.toFixed(3)}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Cart Summary */}
            <Typography variant="h5" gutterBottom fontWeight={600}>
                Your Order ({items.length} items)
            </Typography>

            <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 4 }}>
                {items.length === 0 ? (
                    <Typography color="text.secondary">Your cart is empty</Typography>
                ) : (
                    <Box>
                        {items.map((item, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 2,
                                    borderBottom: "1px solid #eee",
                                    pb: 1,
                                }}
                            >
                                <ListItemText
                                    primary={item.name}
                                    secondary={`Qty: ${item.quantity}`}
                                />
                                <Box>
                                    <Button
                                        size="small"
                                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                    >
                                        +
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={() =>
                                            updateQuantity(
                                                item.id,
                                                Math.max(1, item.quantity - 1)
                                            )
                                        }
                                    >
                                        -
                                    </Button>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDeleteCartItem(item.id)}
                                    >
                                        <Icon fontSize="small">delete</Icon>
                                    </IconButton>
                                </Box>
                            </Box>
                        ))}

                        <Divider sx={{ my: 2 }} />

                        <Button
                            variant="contained"
                            fullWidth
                            disabled={items.length === 0}
                            onClick={handleSendToKitchen}
                            sx={{
                                bgcolor: "#1E8078",
                                "&:hover": {
                                    bgcolor: "#16655C",
                                },
                            }}
                        >
                            Send to Kitchen
                        </Button>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};